import { shiftModalRoutingService } from 'modals/ShiftModal/ShiftModal.ui.context';
import { useFormBehaviors, useURLParameters } from 'hooks';

import {
  UseFormBehaviors,
  UseFormBehaviors_ValidationOptionsType,
} from 'hooks/useFormBehaviors/useFormBehaviors.types';
import { useTranslation } from 'hooks/useTranslation';
import { createContext, useContext, useCallback, useMemo, useState, useEffect } from 'react';
import { API_POST_releasePublishedShifts } from 'requests/POST_releaseShift';
import { toastService } from 'services';
import { useAPIActions } from 'store/reducers/api/apiSlice';
import { handleGenericErrorMessage, firstArrayValue } from 'utils/handleGenericErrorMessage';
import { useShiftModalPropsContext } from 'modals/ShiftModal/ShiftModal.props.context';
import { castStringToInteger } from 'utils';
import { ReleaseWithTimeOffPayloadType } from 'requests/index.types';
import {
  defaultEndTime,
  defaultStartTime,
  titleOptions,
} from 'modals/TimeOffModal/TimeOffModal.constants';
import {
  VALIDATION_durationMin1Hr,
  VALIDATION_endDate,
  VALIDATION_without_msg,
} from 'modals/TimeOffModal/TimeOffModalView.validation.constants';
import { combineMsgs } from 'validations/index.utils';
import { DateTime } from 'luxon';
import { CustomField } from 'types/graphql';
import { useShiftModalDataContext } from 'modals/ShiftModal/ShiftModalProvider.data.context';
import { getCurrentlySelectedLocation } from 'utils/getCurrentlySelectedLocation';

export type ReleaseType = {
  reason_category: string;
  reason_message: string;
  recurring: string;
  dontAddTimeOff: boolean;
  notes: string;
  title: string[];
  allDay: boolean;
  startDate: string[];
  startTime: string[];
  endDate: string[];
  endTime: string[];
  [key: string]: string | string[] | boolean | undefined;
};

type ReleaseShiftViewContextType = UseFormBehaviors<ReleaseType> & {
  submitRelease: (timeOffPayload?: ReleaseWithTimeOffPayloadType) => Promise<void>;
  isSubmitting: boolean;
  isDisabled: boolean;
  timeOffCustomFields: CustomField[];
};

export const ReleaseShiftViewContext = createContext({} as ReleaseShiftViewContextType);

type ReleaseShiftViewProviderProps = {
  children: React.ReactNode;
};

export const ReleaseShiftViewProvider: React.FC<ReleaseShiftViewProviderProps> = ({ children }) => {
  const { apiRequest } = useAPIActions();
  const { data } = useShiftModalDataContext();
  const order = data?.order;

  const selectedLocation = getCurrentlySelectedLocation();

  const { refreshBackgroundPage } = useShiftModalPropsContext();
  const { t } = useTranslation();
  const [{ conflictedShiftId }, { removeParam }] =
    useURLParameters<{
      conflictedShiftId?: string;
    }>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const timeOffCustomFields = useMemo<CustomField[]>(() => {
    return (order?.availableCustomFields as CustomField[]) || [];
  }, [order?.availableCustomFields]);

  const shiftStartAt = order?.startAt;
  const shiftEndAt = order?.endAt;
  const time_zone = selectedLocation?.timeZone ?? undefined;

  const defaultDate = useMemo(() => {
    if (shiftStartAt) return DateTime.fromISO(shiftStartAt).toString();
    return DateTime.now().toString();
  }, [shiftStartAt]);

  const defaultEndDate = useMemo(() => {
    if (!shiftEndAt) return undefined;
    const rawEndAt = DateTime.fromISO(shiftEndAt, { zone: 'utc' });
    const endAtWithTimeZone = time_zone ? rawEndAt.setZone(time_zone) : rawEndAt;
    return endAtWithTimeZone?.toString();
  }, [shiftEndAt, time_zone]);

  const defaultStartAt = useMemo(() => {
    if (!shiftStartAt) return undefined;
    const rawStartAt = DateTime.fromISO(shiftStartAt, { zone: 'utc' });
    const startAtWithTimeZone = time_zone ? rawStartAt.setZone(time_zone) : rawStartAt;
    return startAtWithTimeZone.toFormat('HH:mm:ss');
  }, [shiftStartAt, time_zone]);

  const defaultEndAt = useMemo(() => {
    if (!shiftEndAt) return undefined;
    const rawEndAt = DateTime.fromISO(shiftEndAt, { zone: 'utc' });
    const endAtWithTimeZone = time_zone ? rawEndAt.setZone(time_zone) : rawEndAt;
    return endAtWithTimeZone.toFormat('HH:mm:ss');
  }, [shiftEndAt, time_zone]);

  const orderDisplayId = data?.order?.id;
  const handleRelease = useCallback(
    async (values: ReleaseType, timeOffPayload?: ReleaseWithTimeOffPayloadType) => {
      const shiftIdToRelease = !!conflictedShiftId
        ? castStringToInteger(conflictedShiftId)
        : orderDisplayId;
      if (!shiftIdToRelease) return;

      setIsSubmitting(true);

      try {
        await apiRequest(
          API_POST_releasePublishedShifts({
            ...values,
            shift_id: shiftIdToRelease,
            time_off: timeOffPayload,
          }),
          {
            onSuccess: () => {
              toastService.success(t('success:SHIFT_RELEASED'));
              refreshBackgroundPage();

              if (conflictedShiftId) {
                removeParam('conflictedShiftId');
                shiftModalRoutingService.goToView('AwardTeamMemberView');
              } else {
                shiftModalRoutingService.close();
              }
            },
            onError: (e) => {
              const nestedMessage =
                e?.data?.error_messages && typeof e.data.error_messages === 'object'
                  ? firstArrayValue(e.data.error_messages)
                  : null;

              const errorToHandle = nestedMessage
                ? { ...e, data: { ...e.data, message: nestedMessage } }
                : e;

              handleGenericErrorMessage(
                errorToHandle,
                t('errors:SOMETHING_WENT_WRONG_RELEASING_SHIFT'),
              );
            },
          },
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [apiRequest, refreshBackgroundPage, orderDisplayId, t, conflictedShiftId, removeParam],
  );

  const formUtils = useFormBehaviors<ReleaseType>({
    initialState: {
      reason_category: '',
      reason_message: '',
      recurring: '',
      dontAddTimeOff: false,
      notes: '',
      title: [titleOptions[0].value],
      allDay: true,
      startDate: [defaultDate],
      startTime: [defaultStartAt || defaultStartTime],
      endDate: [defaultEndDate || defaultDate],
      endTime: [defaultEndAt || defaultEndTime],
    },
    validations: {
      startDate: VALIDATION_without_msg,
      startTime: VALIDATION_without_msg,
      endDate: VALIDATION_endDate,
      endTime: combineMsgs(VALIDATION_without_msg, VALIDATION_durationMin1Hr),
    } as unknown as UseFormBehaviors_ValidationOptionsType<ReleaseType>,
    onSubmit: handleRelease,
    isDirtyCheckEnabled: true,
  });

  useEffect(() => {
    if (!timeOffCustomFields.length) return;
    timeOffCustomFields.forEach((field) => {
      if (formUtils.values[field.name] !== undefined) return;
      const shouldPrefill =
        field.fieldType === 'dropdown' && field.prefillDefault && field.customFieldOptions?.length;
      let defaultValue: string | string[];
      if (shouldPrefill) {
        defaultValue = [field.customFieldOptions?.[0]?.value ?? ''];
      } else if (field.fieldType === 'dropdown') {
        return;
      } else {
        defaultValue = '';
      }
      formUtils.onChange({ target: { name: field.name, value: defaultValue } });
    });
  }, [timeOffCustomFields, formUtils]);

  const handleChange = useCallback(
    (event) => {
      const newValue = { ...event };
      if (event.target.name === 'reason_category') newValue.target.value = newValue.target.value[0];
      formUtils.onChange(newValue);
    },
    [formUtils],
  );

  const isDisabled =
    formUtils.values.reason_category === '' ||
    (formUtils.values.reason_category === 'other' && formUtils.values.reason_message === '');

  const submitRelease = useCallback(
    async (timeOffPayload?: ReleaseWithTimeOffPayloadType) => {
      await handleRelease(formUtils.values, timeOffPayload);
    },
    [formUtils.values, handleRelease],
  );

  const value = useMemo(() => {
    return {
      ...formUtils,
      onChange: handleChange,
      isDisabled,
      submitRelease,
      isSubmitting,
      timeOffCustomFields,
    };
  }, [formUtils, handleChange, isDisabled, submitRelease, isSubmitting, timeOffCustomFields]);

  return (
    <ReleaseShiftViewContext.Provider value={value}>{children}</ReleaseShiftViewContext.Provider>
  );
};

export const useReleaseShiftViewContext = (): ReleaseShiftViewContextType =>
  useContext(ReleaseShiftViewContext);
